// Fourniture d'un complément demandé par le service : motif, pièces manquantes ou à remplacer
// (appareil ou coffre), réponses du formulaire à corriger, message au service (POST /complement).
import { validerDonnees, type DonneesFormulaire } from '@cc/contrats';
import { Bandeau, Bouton, Formulaire, ZoneTexte } from '@cc/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date } from '../../../commun/format';
import { useMessages } from '../../../commun/Messages';
import { useDossierUsager, useFicheDemarche } from '../api';
import { Bloc, EnTeteCitoyen, ExigeConnexion } from '../commun';
import { donneesInitiales, erreursPourResume, FormulaireDynamique, sansErreursDe } from '../depot/FormulaireDynamique';
import { erreursFormulaireApi, tarifsDe, versFormulaire, type ErreurSaisie } from '../depot/formulaire-depuis-fiche';
import { SelecteurPieces } from '../depot/SelecteurPieces';
import { useDepot, type PieceCoffreChoisie } from '../depot/useDepot';
import type { FicheDossierUsager } from '../types';
import commun from '../citoyen.module.css';

function Complement({ d }: { d: FicheDossierUsager }) {
  const navigate = useNavigate();
  const { afficher } = useMessages();
  const fiche = useFicheDemarche(d.collectivite.code, d.demarche.code);
  const depot = useDepot(d.id);
  const formulaire = useMemo(() => (fiche.data ? versFormulaire(fiche.data) : null), [fiche.data]);
  const tarifs = useMemo(() => (fiche.data ? tarifsDe(fiche.data) : {}), [fiche.data]);
  const [donnees, setDonnees] = useState<DonneesFormulaire>({});
  const [erreurs, setErreurs] = useState<ErreurSaisie[]>([]);
  const [pieces, setPieces] = useState<PieceCoffreChoisie[]>([]);
  const [message, setMessage] = useState('');
  const [corriger, setCorriger] = useState(false);
  const initialise = useRef(false);
  useEffect(() => {
    if (!formulaire || initialise.current) return;
    initialise.current = true;
    setDonnees(donneesInitiales(formulaire, d.donnees_formulaire));
  }, [formulaire, d.donnees_formulaire]);

  const envoyer = async () => {
    if (!formulaire) return;
    const r = validerDonnees(formulaire, donnees, { tarifs });
    const bloquantes = r.erreurs.filter((e) => !(e.message.endsWith('champ obligatoire') && formulaire.sections.some((s) => s.champs.some((c) => c.code === e.champ && c.type === 'fichier'))));
    setErreurs(bloquantes);
    if (bloquantes.length > 0) {
      setCorriger(true);
      return;
    }
    try {
      await depot.completer(donnees, pieces, message);
      afficher('succes', 'Complément transmis au service : votre demande repasse en vérification.');
      navigate(`/citoyen/mes-dossiers/${d.id}`);
    } catch (e) {
      const parChamp = erreursFormulaireApi(e);
      if (parChamp.length > 0) {
        setErreurs(parChamp);
        setCorriger(true);
      }
    }
  };

  if (!d.actions_possibles.includes('completer')) {
    return (
      <Bandeau type="info" titre="Aucun complément n'est attendu">
        Cette demande n&apos;est pas en attente de complément. Son état est visible sur la fiche de suivi.
      </Bandeau>
    );
  }
  const piecesDossier = d.pieces.map((p) => ({ id: p.document_id, titre: p.titre, type: p.type }));
  const autresErreurs = depot.detailsErreur.filter((x) => !x.champ.startsWith('donnees_formulaire.'));
  return (
    <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} fonction="La démarche" lignes={5}>
      {formulaire ? (
        <Formulaire
          libelle="Complément"
          onSoumettre={() => void envoyer()}
          erreurs={erreursPourResume(erreurs, 'complement')}
          actions={
            <Bouton type="submit" chargement={depot.enCours === 'completer'} libelleChargement="Envoi en cours" pleineLargeur>
              Transmettre le complément
            </Bouton>
          }
        >
          <Bloc etiquette="Ce que le service demande" titre={d.complement?.motif ?? 'Complément demandé'}>
            <p className={commun.blocTexte}>
              Demandé le {date(d.complement?.demande_le)}.{d.complement?.relance_le ? ` Relance envoyée le ${date(d.complement.relance_le)}.` : ''} Sans réponse, la demande sera classée sans suite dans le délai prévu par la démarche.
            </p>
          </Bloc>
          {depot.messageErreur && erreurs.length === 0 ? (
            <Bandeau type="erreur">
              {depot.messageErreur}
              {autresErreurs.length > 0 ? (
                <ul>
                  {autresErreurs.map((x) => (
                    <li key={x.champ}>{x.message}</li>
                  ))}
                </ul>
              ) : null}
            </Bandeau>
          ) : null}
          <section className={commun.pile} aria-labelledby="complement-pieces">
            <h2 id="complement-pieces">Pièces</h2>
            <SelecteurPieces dossier={d} enAttente={pieces} onCoffre={(p) => setPieces((l) => [...l.filter((x) => x.piece_id !== p.piece_id), p])} onFichier={(f, type) => depot.televerser(f, type)} desactive={depot.enCours !== null} />
          </section>
          <section className={commun.pile} aria-labelledby="complement-reponses">
            <h2 id="complement-reponses">Vos réponses</h2>
            {corriger ? (
              <FormulaireDynamique
                formulaire={formulaire}
                donnees={donnees}
                onChange={setDonnees}
                onChampModifie={(code) => setErreurs((e) => sansErreursDe(e, code))}
                erreurs={erreurs}
                tarifs={tarifs}
                pieces={piecesDossier}
                prefixe="complement"
              />
            ) : (
              <div>
                <Bouton variante="secondaire" onClick={() => setCorriger(true)}>
                  Corriger une réponse du formulaire
                </Bouton>
              </div>
            )}
          </section>
          <ZoneTexte id="complement-message" libelle="Message au service" value={message} onChange={(e) => setMessage(e.target.value)} facultatif rows={3} maxLength={2000} aide="Visible par le service avec le complément" />
        </Formulaire>
      ) : null}
    </EtatRequete>
  );
}

export function PageComplement() {
  const { id = '' } = useParams();
  const dossier = useDossierUsager(id || null);
  return (
    <>
      <EnTeteCitoyen titre="Fournir le complément" surTitre={dossier.data ? `${dossier.data.demarche.libelle} · ${dossier.data.numero ?? ''}` : undefined} retour={`/citoyen/mes-dossiers/${id}`} libelleRetour="Suivi de la demande" />
      <ExigeConnexion>
        <EtatRequete chargement={dossier.isPending} erreur={dossier.error} onReessayer={() => void dossier.refetch()} fonction="Cette demande" lignes={6}>
          {dossier.data ? <Complement d={dossier.data} /> : null}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}
